<script setup lang="ts">
import { computed, ref } from 'vue'
import { WdsHeading, WdsModal, WdsStack } from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { NetworkError } from '@/utils/notification'
import enums from '@/utils/enum'

type MspTermsPrompt = {
  globalId: string
  name: string | null
  logo: string | null
  termsUrl: string | null
  privacyUrl: string | null
}

// Injected by the layout at render; when present there is something to accept.
const mspTerms = (currentUser?.msp_terms ?? null) as MspTermsPrompt | null

const mspName = mspTerms?.name ?? ''
const mspLogo = mspTerms?.logo ?? null
const termsUrl = mspTerms?.termsUrl ?? null
const privacyUrl = mspTerms?.privacyUrl ?? null

const show = ref(mspTerms !== null)
const submitting = ref(false)
const acceptedTerms = ref(false)
const acceptedPrivacy = ref(false)

// Only the documents that exist need to be ticked.
const canAgree = computed(
  () => (!termsUrl || acceptedTerms.value) && (!privacyUrl || acceptedPrivacy.value),
)

const introText = computed(() =>
  $t(
    'To work with :name on Worksome, you must review and accept our documents below before you continue. Please open each one, then confirm that you have read and agree to it.',
    { name: mspName },
  ),
)

const confirmationText = computed(() => $t('By selecting "I confirm" you agree to the statements above.'))

const csrfToken = (): string =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''

const accept = async (): Promise<void> => {
  if (submitting.value || !canAgree.value || mspTerms === null) {
    return
  }

  submitting.value = true

  try {
    const response = await fetch(`/msp-terms/${mspTerms.globalId}/accept`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-TOKEN': csrfToken(),
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Unexpected response ${response.status}`)
    }

    // Reload rather than just close: the render check runs again, so any
    // other pending step (a second MSP's terms) shows right away.
    window.location.reload()
  } catch (e: any) {
    new NetworkError({
      severity: enums.notification.severity.CRITICAL,
      ex: e,
    }).dispatch()
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <WdsModal
    :show="show"
    md
    no-close-icon
    disable-click-outside
    class="msp-terms-modal !tw-overflow-y-hidden"
  >
    <template #header>
      <WdsStack gap-sm align-center>
        <img v-if="mspLogo" :src="mspLogo" :alt="mspName" class="msp-terms-logo" />
        <WdsHeading v-else class="tw-my-0" type="title2">
          {{ mspName }}
        </WdsHeading>
      </WdsStack>
    </template>

    <div class="tw-max-h-[70vh] tw-overflow-y-auto">
      <p class="tw-mb-5">{{ introText }}</p>

      <div class="tw-flex tw-flex-col tw-gap-3 tw-mb-5">
        <label v-if="termsUrl" class="msp-terms-doc">
          <input v-model="acceptedTerms" type="checkbox" />
          <span>
            {{ $t('I have read and accept the') }}
            <a :href="termsUrl" target="_blank" rel="noopener">{{ $t('Terms of Use') }}</a>
          </span>
        </label>
        <label v-if="privacyUrl" class="msp-terms-doc">
          <input v-model="acceptedPrivacy" type="checkbox" />
          <span>
            {{ $t('I have read the') }}
            <a :href="privacyUrl" target="_blank" rel="noopener">{{ $t('Privacy Statement') }}</a>
          </span>
        </label>
      </div>

      <p class="tw-text-sm tw-text-gray-500 tw-mb-5">{{ confirmationText }}</p>

      <div class="tw-flex tw-justify-center">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!canAgree || submitting"
          @click="accept"
        >
          {{ $t('I confirm') }}
        </button>
      </div>
    </div>
  </WdsModal>
</template>

<style scoped lang="scss">
.msp-terms-modal {
  :deep(.wds-modal-header) {
    @apply tw-py-6 tw-px-6 !important;
    @apply tw-border-b !important;
    @apply tw-border-gray-200 !important;
  }
}

.msp-terms-logo {
  max-height: 34px;
  width: auto;
}

.msp-terms-doc {
  @apply tw-flex tw-items-start tw-gap-2 tw-cursor-pointer tw-m-0;

  input {
    @apply tw-mt-1 tw-shrink-0;
  }
}
</style>
